import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CadsBadgeBitComponent } from '../cads-badge-bit/cads-badge-bit.component';
import type { CadsBadgeBitConfig } from '../cads-badge-bit/cads-badge-bit.config';

import { CadsHeadingDetailBitConfig } from './cads-heading-detail-bit.config';

/**
 * Stacked heading block: optional **eyebrow**, **title** + optional inline **badge**, then optional **subtitle**.
 * **small** tightens title/subtitle; **medium** (default) and **large** use the same CSS scale (see
 * `cads-form-controls.scss` tokens) so copy lines up with {@link CadsInputComponent}. Badge uses
 * {@link CadsBadgeBitComponent} at **xs** with a
 * status dot when no icon is set.
 * Title and subtitle use semantic text tokens (`--cads-color-text`, `--cads-color-text-muted`)—no raw hex **tint** inputs (see
 * `libs/angular-cads/REQUIREMENTS.md`, *Design handoff*).
 */
@Component({
    selector: 'cads-heading-detail-bit',
    imports: [CadsBadgeBitComponent],
    templateUrl: './cads-heading-detail-bit.component.html',
    styleUrl: './cads-heading-detail-bit.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CadsHeadingDetailBitComponent {
  readonly config = input.required<CadsHeadingDetailBitConfig>();

  readonly effectiveConfig = computed(() => {
    const c = this.config();
    return {
      title: c.title,
      eyebrow: c.eyebrow,
      subtitle: c.subtitle,
      size: c.size ?? 'medium',
      badgeLabel: c.badgeLabel,
      badgeColor: c.badgeColor ?? 'mid-gray',
    };
  });

  /** Badge config for {@link CadsBadgeBitComponent}, or **null** when no trimmed {@link CadsHeadingDetailBitConfig.badgeLabel}. */
  readonly badgeBitConfig = computed((): CadsBadgeBitConfig | null => {
    const e = this.effectiveConfig();
    const label = e.badgeLabel?.trim();
    if (!label) {
      return null;
    }
    return {
      label,
      color: e.badgeColor,
      size: 'xs',
    };
  });

  readonly isSmall = computed(() => this.effectiveConfig().size === 'small');
}
